/**
 * lightweight-charts primitives that draw order flow on the canvas:
 *  - OrderflowPrimitive (attached to the price series): footprint cells (bid x ask), value
 *    area, POC, imbalance colours, Orderflows Trader paint (boxes, divergence lines, zones),
 *    volume profile on the right and the market profile (TPO) per day.
 *  - BarStatsPrimitive (attached to a blank series in its own pane): the Bar Statistics table.
 * All drawing is in media (CSS pixel) coordinates, the same units the chart's
 * priceToCoordinate / timeToCoordinate return.
 */

import { OF_COLORS } from "./orderflowSignals";

const FONT = '"Avenir Next", "Trebuchet MS", sans-serif';
const fmtVol = (v) => (v >= 10000 ? `${Math.round(v / 1000)}k` : String(v));

function visibleBars(chart, bars) {
  const ts = chart.timeScale();
  const width = ts.width();
  const spacing = ts.options().barSpacing;
  const out = [];
  bars.forEach((b) => {
    const x = ts.timeToCoordinate(b.time);
    if (x == null || x < -spacing || x > width + spacing) return;
    out.push({ b, x });
  });
  return { list: out, spacing };
}

class OrderflowRenderer {
  constructor(source) {
    this._s = source;
  }

  draw(target) {
    const s = this._s;
    if (!s.chart || !s.series) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const y = (p) => s.series.priceToCoordinate(p);
      // Market Profile comes from the candles, so it draws even without order-flow ticks.
      if (s.opts.marketProfile && s.tpo?.length) this._tpo(ctx, y, mediaSize);
      if (!s.bars.length) return;
      const { list, spacing } = visibleBars(s.chart, s.bars);
      const rowPx = Math.abs((y(0) ?? 0) - (y(s.rowSize) ?? 0)) || 0;
      if (s.opts.footprint) this._footprint(ctx, list, spacing, y, rowPx);
      if (s.opts.signalsPaint) this._paint(ctx, list, spacing, y, rowPx, mediaSize);
      if (s.opts.volumeProfile && s.profile) this._profile(ctx, y, rowPx, mediaSize);
    });
  }

  _footprint(ctx, list, spacing, y, rowPx) {
    const s = this._s;
    const showText = spacing >= 46 && rowPx >= 9;
    const fontPx = Math.max(8, Math.min(12, rowPx - 2));
    ctx.font = `${fontPx}px ${FONT}`;
    ctx.textBaseline = "middle";
    list.forEach(({ b, x }) => {
      const w = Math.max(2, spacing * 0.92);
      const left = x - w / 2;
      const maxVol = Math.max(1, ...b.cells.map((c) => c.bid + c.ask));
      b.cells.forEach((c) => {
        const yc = y(c.price + s.rowSize / 2);
        if (yc == null) return;
        const h = Math.max(1, rowPx - 1);
        const top = yc - h / 2;
        if (showText) {
          // tinted by delta, stronger with more volume
          const d = c.ask - c.bid;
          const a = 0.12 + 0.45 * ((c.bid + c.ask) / maxVol);
          ctx.fillStyle = d >= 0 ? `rgba(34,197,94,${a.toFixed(2)})` : `rgba(239,68,68,${a.toFixed(2)})`;
          if (s.opts.valueArea && c.inValueArea) ctx.fillStyle = `rgba(148,163,184,${(a + 0.1).toFixed(2)})`;
          ctx.fillRect(left, top, w, h);
          if (c.poc) {
            ctx.strokeStyle = b.paint.poc || OF_COLORS.yellow;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(left + 0.5, top + 0.5, w - 1, h - 1);
          }
          ctx.textAlign = "right";
          ctx.fillStyle = c.sellImb ? OF_COLORS.bear : "#e2e8f0";
          ctx.fillText(fmtVol(c.bid), x - 3, yc);
          ctx.textAlign = "left";
          ctx.fillStyle = c.buyImb ? OF_COLORS.blue : "#e2e8f0";
          ctx.fillText(fmtVol(c.ask), x + 3, yc);
          ctx.fillStyle = "rgba(226,232,240,0.5)";
          ctx.fillRect(x - 0.5, top + 1, 1, h - 2);
          if (c.thin && s.opts.signals.has("thinPrints")) {
            ctx.strokeStyle = OF_COLORS.yellow;
            ctx.setLineDash([2, 2]);
            ctx.strokeRect(left + 1, top + 1, w - 2, h - 2);
            ctx.setLineDash([]);
          }
        } else if (spacing >= 10 && rowPx >= 2) {
          // medium zoom: delta-tinted cells, no numbers (zoom in to read them)
          const d = c.ask - c.bid;
          const a = 0.1 + 0.5 * ((c.bid + c.ask) / maxVol);
          ctx.fillStyle = d >= 0 ? `rgba(34,197,94,${a.toFixed(2)})` : `rgba(239,68,68,${a.toFixed(2)})`;
          ctx.fillRect(left, top, w, h);
          if (c.buyImb) {
            ctx.fillStyle = OF_COLORS.blue;
            ctx.fillRect(left + w - 2, top, 2, h);
          }
          if (c.sellImb) {
            ctx.fillStyle = OF_COLORS.bear;
            ctx.fillRect(left, top, 2, h);
          }
          if (c.poc) {
            ctx.strokeStyle = b.paint.poc || OF_COLORS.yellow;
            ctx.lineWidth = 1;
            ctx.strokeRect(left + 0.5, top + 0.5, w - 1, Math.max(1, h - 1));
          }
        } else {
          // zoomed out: imbalance ticks and the POC only, candles stay readable
          if (c.buyImb) {
            ctx.fillStyle = OF_COLORS.blue;
            ctx.fillRect(x + w / 2 - 2, top, 2, h);
          }
          if (c.sellImb) {
            ctx.fillStyle = OF_COLORS.bear;
            ctx.fillRect(x - w / 2, top, 2, h);
          }
          if (c.poc) {
            ctx.fillStyle = b.paint.poc || OF_COLORS.yellow;
            ctx.fillRect(left, yc - 1, w, 2);
          }
        }
      });
    });
  }

  _paint(ctx, list, spacing, y, rowPx, mediaSize) {
    const s = this._s;
    const on = (k) => s.opts.signals.has(k);
    const ts = s.chart.timeScale();
    list.forEach(({ b, x }) => {
      const w = Math.max(3, spacing * 0.92);
      const yHi = y(b.high + s.rowSize);
      const yLo = y(b.low);
      if (yHi == null || yLo == null) return;
      if (b.paint.box && on("multipleImbalance")) {
        ctx.strokeStyle = b.paint.box;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x - w / 2 - 1, yHi - 1, w + 2, yLo - yHi + 2);
      }
      if (b.paint.line && on("deltaDivergence")) {
        ctx.strokeStyle = b.paint.line;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, mediaSize.height);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (b.paint.candle && on("engulfingVA")) {
        ctx.fillStyle = b.paint.candle;
        ctx.globalAlpha = 0.35;
        ctx.fillRect(x - w / 2, yHi, w, yLo - yHi);
        ctx.globalAlpha = 1;
      }
      if (!s.opts.footprint && on("valueArea") && b.vah != null) {
        const a = y(b.vah + s.rowSize);
        const z = y(b.val);
        if (a != null && z != null) {
          ctx.fillStyle = "rgba(148,163,184,0.18)";
          ctx.fillRect(x - w / 2, a, w, z - a);
        }
      }
      if (!s.opts.footprint && b.paint.poc && (on("slingshotPoc") || on("prominentPoc"))) {
        const yp = y(b.poc + s.rowSize / 2);
        if (yp != null) {
          ctx.fillStyle = b.paint.poc;
          ctx.fillRect(x - w / 2, yp - 1.5, w, 3);
        }
      }
      b.signals.forEach((sig) => {
        if (!sig.zone || !on(sig.key)) return;
        const until = sig.zone.until != null ? ts.timeToCoordinate(sig.zone.until) : null;
        const x2 = until != null ? until : mediaSize.width;
        const y1 = y(sig.zone.to + s.rowSize);
        const y2 = y(sig.zone.from);
        if (y1 == null || y2 == null) return;
        ctx.fillStyle = sig.color;
        ctx.globalAlpha = 0.16;
        ctx.fillRect(x, Math.min(y1, y2), x2 - x, Math.max(2, Math.abs(y2 - y1)));
        ctx.globalAlpha = 1;
      });
    });
  }

  _profile(ctx, y, rowPx, mediaSize) {
    const s = this._s;
    const p = s.profile;
    if (!p.rows.length) return;
    const maxVol = Math.max(1, ...p.rows.map((r) => r.bid + r.ask));
    const maxW = mediaSize.width * 0.22;
    const right = mediaSize.width - 4;
    const h = Math.max(1, rowPx - 1);
    p.rows.forEach((r) => {
      const yc = y(r.price + s.rowSize / 2);
      if (yc == null) return;
      const total = r.bid + r.ask;
      const wTotal = (total / maxVol) * maxW;
      const wBuy = total ? (r.ask / total) * wTotal : 0;
      const inVA = r.price >= p.val - 1e-12 && r.price <= p.vah + 1e-12;
      ctx.globalAlpha = inVA ? 0.55 : 0.3;
      ctx.fillStyle = "#3b82f6";
      ctx.fillRect(right - wTotal, yc - h / 2, wBuy, h);
      ctx.fillStyle = "#f97316";
      ctx.fillRect(right - wTotal + wBuy, yc - h / 2, wTotal - wBuy, h);
    });
    ctx.globalAlpha = 1;
    const line = (price, color, dash) => {
      const yy = y(price + s.rowSize / 2);
      if (yy == null) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash(dash);
      ctx.beginPath();
      ctx.moveTo(right - maxW, yy);
      ctx.lineTo(right, yy);
      ctx.stroke();
      ctx.setLineDash([]);
    };
    line(p.poc, OF_COLORS.yellow, []);
    line(p.vah, OF_COLORS.gray, [4, 3]);
    line(p.val, OF_COLORS.gray, [4, 3]);
  }

  /** One TPO profile per session, drawn from the session's first candle to the right. */
  _tpo(ctx, y, mediaSize) {
    const s = this._s;
    const ts = s.chart.timeScale();
    const width = mediaSize.width;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    s.tpo.forEach((sess, i) => {
      const x0 = ts.timeToCoordinate(sess.firstTime);
      const xEnd = ts.timeToCoordinate(sess.lastTime);
      if (x0 == null && xEnd == null) return;
      const left = x0 ?? 0;
      const next = s.tpo[i + 1];
      const nextX = next ? ts.timeToCoordinate(next.firstTime) : null;
      const room = Math.max(24, (nextX ?? Math.max(xEnd ?? left, left) + 200) - left - 4);
      if (left > width || left + room < 0) return;
      const rowPx = Math.abs((y(0) ?? 0) - (y(sess.rowSize) ?? 0));
      const maxLetters = Math.max(1, ...sess.rows.map((r) => r.letters.length));
      const letterW = Math.max(2, Math.min(9, room / maxLetters));
      const showText = rowPx >= 8 && letterW >= 6;
      const fontPx = Math.max(7, Math.min(11, rowPx - 1));
      ctx.font = `${fontPx}px ${FONT}`;
      const inVA = (p) => p >= sess.val - 1e-12 && p <= sess.vah + 1e-12;
      // light background so the profile reads on top of the candles
      const top = y(sess.high + sess.rowSize);
      const bottom = y(sess.low);
      if (top != null && bottom != null) {
        ctx.fillStyle = "rgba(2, 6, 23, 0.35)";
        ctx.fillRect(left, top, Math.min(room, maxLetters * letterW + 4), bottom - top);
      }
      sess.rows.forEach((r) => {
        const yc = y(r.price + sess.rowSize / 2);
        if (yc == null || yc < -rowPx || yc > mediaSize.height + rowPx) return;
        const isPoc = Math.abs(r.price - sess.poc) < 1e-12;
        const color = isPoc ? OF_COLORS.yellow : inVA(r.price) ? "rgba(147,197,253,0.9)" : "rgba(148,163,184,0.7)";
        if (showText) {
          ctx.fillStyle = color;
          for (let k = 0; k < r.letters.length; k += 1) ctx.fillText(r.letters[k], left + 2 + k * letterW, yc);
        } else {
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.75;
          ctx.fillRect(left + 2, yc - Math.max(1, rowPx - 1) / 2, r.letters.length * letterW, Math.max(1, rowPx - 1));
          ctx.globalAlpha = 1;
        }
      });
      // POC line across the session, value area edges, initial balance on the left edge
      const line = (price, color, dash, w) => {
        const yy = y(price);
        if (yy == null) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = w;
        ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(left, yy);
        ctx.lineTo(left + room, yy);
        ctx.stroke();
        ctx.setLineDash([]);
      };
      if (sess.poc != null) line(sess.poc + sess.rowSize / 2, "rgba(250,204,21,0.8)", [], 1);
      if (sess.vah != null) line(sess.vah + sess.rowSize, "rgba(147,197,253,0.6)", [4, 3], 1);
      if (sess.val != null) line(sess.val, "rgba(147,197,253,0.6)", [4, 3], 1);
      if (sess.ibHigh != null) {
        const a = y(sess.ibHigh);
        const z = y(sess.ibLow);
        if (a != null && z != null) {
          ctx.fillStyle = "#38bdf8";
          ctx.fillRect(left - 3, a, 2, z - a);
        }
      }
    });
  }
}

export class OrderflowPrimitive {
  constructor() {
    this.chart = null;
    this.series = null;
    this.bars = [];
    this.rowSize = 0;
    this.profile = null;
    this.tpo = null;
    this.opts = { footprint: false, signalsPaint: false, volumeProfile: false, marketProfile: false, valueArea: false, signals: new Set() };
    this._requestUpdate = null;
    this._views = [{ zOrder: () => "top", renderer: () => new OrderflowRenderer(this) }];
  }

  attached({ chart, series, requestUpdate }) {
    this.chart = chart;
    this.series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this.chart = null;
    this.series = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return this._views;
  }

  setState(next) {
    Object.assign(this, next);
    this._requestUpdate?.();
  }
}

class BarStatsRenderer {
  constructor(source) {
    this._s = source;
  }

  draw(target) {
    const s = this._s;
    if (!s.chart || !s.bars.length) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const { list, spacing } = visibleBars(s.chart, s.bars);
      const fields = [
        ["Volume", (st) => st.volume],
        ["Delta", (st) => st.delta, "deltaColor"],
        ["Max Δ", (st) => st.maxDelta, "maxDeltaColor"],
        ["Min Δ", (st) => st.minDelta, "minDeltaColor"],
        ["Cum Δ", (st) => st.cumDelta],
        ["Δ/Vol", (st) => `${Math.round(st.deltaPerVolume * 100)}%`],
      ];
      const rowH = Math.max(12, Math.min(18, (mediaSize.height - 4) / fields.length));
      ctx.font = `${Math.min(11, rowH - 3)}px ${FONT}`;
      ctx.textBaseline = "middle";
      const showValues = spacing >= 28;
      list.forEach(({ b, x }) => {
        fields.forEach(([, get, paintKey], i) => {
          const yc = 2 + rowH * i + rowH / 2;
          const v = get(b.stats);
          const num = typeof v === "number" ? v : b.stats.delta;
          let color = num > 0 ? "rgba(34,197,94,0.25)" : num < 0 ? "rgba(239,68,68,0.25)" : "rgba(148,163,184,0.12)";
          if (paintKey && b.paint[paintKey] && s.signals.has(paintKey === "deltaColor" ? "extremeDelta" : "smallMinMaxDelta")) {
            color = b.paint[paintKey];
          }
          if (i === 0) color = "rgba(148,163,184,0.15)";
          ctx.fillStyle = color;
          ctx.fillRect(x - spacing / 2 + 1, yc - rowH / 2 + 1, spacing - 2, rowH - 2);
          if (showValues) {
            ctx.fillStyle = "#e2e8f0";
            ctx.textAlign = "center";
            ctx.fillText(typeof v === "number" ? fmtVol(v) : v, x, yc);
          }
        });
      });
      // row names on the left edge
      ctx.textAlign = "left";
      fields.forEach(([name], i) => {
        const yc = 2 + rowH * i + rowH / 2;
        ctx.fillStyle = "rgba(15,23,42,0.75)";
        ctx.fillRect(0, yc - rowH / 2 + 1, 46, rowH - 2);
        ctx.fillStyle = "#cbd5e1";
        ctx.fillText(name, 4, yc);
      });
    });
  }
}

export class BarStatsPrimitive {
  constructor() {
    this.chart = null;
    this.bars = [];
    this.signals = new Set();
    this._requestUpdate = null;
    this._views = [{ zOrder: () => "top", renderer: () => new BarStatsRenderer(this) }];
  }

  attached({ chart, requestUpdate }) {
    this.chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this.chart = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return this._views;
  }

  setState(next) {
    Object.assign(this, next);
    this._requestUpdate?.();
  }
}
